// src/components/Auth/setnicknamepage/SetNicknamePage.js

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from "react-oidc-context";
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import styles from './style/SetNicknamePage.module.css';

export default function SetNicknamePage() {
  const { t } = useTranslation();
  const [nickname, setNickname] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // 1. 중복 제출 방지 상태 추가
  
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const inputRef = useRef(null);

  // 2. 페이지 진입 시 이미 닉네임이 있는지 체크 (선택 사항 - 방어적 코딩)
  // Auth.js를 거쳐서 왔겠지만, 주소창 입력으로 들어온 경우를 대비해 
  // 토큰이 없으면 로그인 페이지로 튕겨내는 로직이 있으면 좋습니다.
  useEffect(() => {
    // 1. 인증 로딩 중이면 대기
    if (auth.isLoading) return;

    // 2. 비로그인 상태면 로그인 페이지로
    if (!auth.isAuthenticated) {
        navigate('/signin', { replace: true });
        return;
    }

    const checkExistingNickname = async () => {
        const userId = auth.user?.profile?.sub;
        const storageKey = `nickname_checked_${userId}`;

        // A. 로컬 스토리지에 이미 있다고 표시된 경우 -> 즉시 홈으로 리다이렉트
        if (localStorage.getItem(storageKey) === 'true') {
            navigate('/', { replace: true });
            return;
        }

        try {
            // B. API로 확실하게 재확인 (방어적 코드)
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user/nickname/me`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${auth.user.id_token}`,
                },
            });

            if (res.status === 200) {
                const data = await res.json();
                if (data.nickname) {
                    // 닉네임이 존재하면 스토리지 업데이트 후 홈으로 이동
                    localStorage.setItem(storageKey, 'true');
                    sessionStorage.setItem('nickname_checked', 'true');
                    // 알림은 선택 사항 (사용자가 당황하지 않게 조용히 이동하는 게 보통 더 좋습니다)
                    // alert("이미 닉네임이 설정되어 있습니다."); 
                    navigate('/', { replace: true });
                }
            }
            // 404인 경우는 닉네임이 없는 것이므로 정상적으로 폼을 보여줌
        } catch (error) {
            console.error("닉네임 확인 중 오류:", error);
            // 에러 시에는 일단 폼을 보여주거나 에러 페이지 처리
        }
    };

    checkExistingNickname();
  }, [auth.isLoading, auth.isAuthenticated, auth.user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 이미 제출 중이면 중단 (따닥 방지)
    if (isSubmitting) return;

    setError('');
    setSuccess(false);
    setIsSubmitting(true); // 버튼 비활성화 시작

    // 공백 제거 및 살균
    const trimmedNickname = nickname.trim();
    const sanitizedNickname = DOMPurify.sanitize(trimmedNickname, { USE_PROFILES: { html: false } });

    // 3. 유효성 검사 강화 (빈 값 체크)
    if (!sanitizedNickname || sanitizedNickname.length < 2) {
      setError(t('setNicknamePage.errorTooShort') || "닉네임은 2글자 이상이어야 합니다.");
      setIsSubmitting(false); // 버튼 활성화 복구
      inputRef.current.focus(); 
      return;
    }

    // (옵션) 특수문자 정규식 체크가 필요하다면 여기에 추가
    // const regex = /^[a-zA-Z0-9가-힣]+$/;
    // if (!regex.test(sanitizedNickname)) { ... }

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/user/nickname/me`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth.user.id_token}`,
          },
          body: JSON.stringify({ nickname: sanitizedNickname }),
        }
      );

      if (res.ok) {
        setSuccess(true);
        
        // 4. 핵심: Auth.js와의 싱크를 위해 세션 스토리지 업데이트
        // 이제 닉네임이 생겼으니, 새로고침 해도 다시 체크하지 않도록 플래그 설정
        sessionStorage.setItem('nickname_checked', 'true'); 
        
        alert(t('setNicknamePage.alertSuccess'));

        const from = location.state?.from || '/'; 
        navigate(from, { replace: true });

      } else {
        const errorData = await res.json();
        // 409 Conflict (이미 존재하는 닉네임) 등 구체적인 에러 처리 가능
        if (res.status === 409) {
            setError(t('setNicknamePage.errorDuplicate') || "이미 사용 중인 닉네임입니다.");
        } else {
            setError(errorData.message || t('setNicknamePage.errorFailed'));
        }
      }
    } catch (err) {
      console.error('닉네임 설정 중 에러:', err);
      setError(t('setNicknamePage.errorNetwork'));
    } finally {
      // 성공했으면 페이지가 이동되므로 false로 돌릴 필요 없지만,
      // 실패했을 경우를 위해 false로 되돌려야 함
      // 성공 시 컴포넌트 언마운트 에러를 방지하기 위해 isMounted 체크를 하기도 하지만,
      // React 최신 버전에서는 큰 문제 안 됨. 안전하게 하려면 아래 조건 추가.
       setSuccess((prev) => {
           if(!prev) setIsSubmitting(false); // 성공 상태가 아닐 때만 버튼 다시 활성화
           return prev;
       });
    }
  };

  return (
    <div className={styles.container}>
      <p className={styles.introText}>{t('setNicknamePage.introText')}</p>
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.inputGroup}>
          <input
            type="text"
            id="nickname"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            className={`${styles.input} ${error ? styles.errorInput : ''}`}
            ref={inputRef}
            required
            maxLength={10}
            disabled={isSubmitting} // 제출 중 입력 방지
            placeholder={t('setNicknamePage.placeholder') || "닉네임을 입력해주세요 (최대 10자)"}
          />
          {error && <p className={styles.error}>{error}</p>}
        </div>
        {success && <p className={styles.success}>{t('setNicknamePage.successMessage')}</p>}
        <button
          type="submit"
          className={styles.button}
          disabled={isSubmitting} // 1. 제출 중 클릭 방지 (핵심)
        >
          {isSubmitting 
            ? (t('setNicknamePage.processing') || "처리 중...") 
            : t('setNicknamePage.buttonText')}
        </button>
      </form>
    </div>
  );
}